import React from 'react';
import styles from './RequestForm.module.scss';
import { digProperty, isEmptyString, isEqualText, isValidEmail, isEmptyArray, isNullOrUndefined } from "../common/utility";
import {
	readOrganisationsByEmailDomain,
	submitUserAccessRequest,
	validateUserAccessRequest,
	prevalidateAccountByEmail
} from "../common/api";

import { useApiServiceContextContext } from "../contexts/apiServiceContext";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { Chip } from "primereact/chip";
import { useTimeout } from "primereact/hooks";

/**
 IOrganisationOption = {
	memberId: "253970",
	memberName: "AC 019Mobile",
	memberStatus: "Active" | "Discontinued" | "Suspended"
}
*/
const COrganisationStatusSuspended = "Suspended";
const COrganisationHelpSuffix = `
We are unable to create an account for this email address at this time. If your organisation is already a GSMA Member and you are a designated contact or Member Information Manager (MIM),
please <a href="https://www.gsma.com/get-involved/gsma-membership/make-a-difference/contact-us/" rel="noreferrer" target="_blank">contact the GSMA Membership Team for More Information</a>.
`;
const COrganisationNoMoreToJoinHelpSuffix = `
There are no organisations you can join at this time. If you need further assistance
please <a href="https://www.gsma.com/get-involved/gsma-membership/make-a-difference/contact-us/" rel="noreferrer" target="_blank">contact the GSMA Membership Team for More Information</a>.
`;
const COrganisationHelpNoneSelected = `Please select an organisation`;
const _CEmailAddressHelpSuffix = `
This email address is already registered with Member Gateway. If you need further assistance
please <a href="https://www.gsma.com/get-involved/gsma-membership/make-a-difference/contact-us/" rel="noreferrer" target="_blank">contact the GSMA Membership Team for More Information</a>.
`;
const CEmailAddressDirectoryMemberHelpSuffix = `
This email address is registered with Member Gateway.
`;

export const RequestForm = ({ title, subTitle, onFormInactivityTimeout, inactivityTimeoutMs }) => {
	const apiServiceContext = useApiServiceContextContext();
	const toast = React.useRef(null);
	const [isWorking, setWorkingState] = React.useState(false);
	const [isSubmitting, setSubmittingState] = React.useState(false);
	const [formActivityCount, setFormActivityCount] = React.useState(1);

	const [userComments, setUserComments] = React.useState("");

	const [userEmail, setUserEmail] = React.useState("");
	const [directoryMemberDetails, setDirectoryMemberDetails] = React.useState(undefined);
	const [emailState, setEmailState] = React.useState({
		isValidating: false,
		isValid: false,
		type: "warning", // "error" | "info" | "warning"
		message: "Please enter a valid email address",
	});
	const [userDisplayName, setUserDisplayName] = React.useState("");
	const [nameState, setNameState] = React.useState({
		isValidating: false,
		isValid: false,
		message: "Please enter your full name including first and last names",
	});

	const [selectedOrganisation, setSelectedOrganisation] = React.useState(undefined);
	const [organisationOptions, setOrganisationOptions] = React.useState([]);	// IOrganisationOption[]
	const [organisationState, setOrganisationState] = React.useState({
		isValidating: false,
		isValid: false,
		message: COrganisationHelpNoneSelected,
	});

	const [resetFormInactivityTimeout] = useTimeout(() => {
		console.debug(`RequestForm:resetFormInactivityTimeout() ➜ Form inactivity timeout reached`);
		onFormInactivityTimeout?.();
	}, inactivityTimeoutMs || 600000, formActivityCount); /// default inactivity timeout is 10 minutes = 600000

	const resetFormInactivity = () => {
		console.debug(`RequestForm:resetFormInactivity() ➜ Resetting form inactivity timeout`);
		resetFormInactivityTimeout();
		setFormActivityCount((prev) => prev + 1);
	};

	React.useEffect(() => {
		console.debug(`RequestForm:useEffect([userEmail]) ➜ userEmail: ${userEmail}`);
		setDirectoryMemberDetails(undefined);
		setUserDisplayName("");
		setNameState({
			isValidating: false,
			isValid: false,
			message: isEmptyString(userEmail) ? "" : "Please enter your full name including first and last names",
		});
		setSelectedOrganisation(null);
		setOrganisationOptions([]);
		setOrganisationState({
			isValidating: false,
			isValid: false,
			message: isEmptyString(userEmail) ? "" : COrganisationHelpNoneSelected,
		});
		setUserComments("");
	}, [userEmail]);

	const resetForm = () => {
		console.debug(`RequestForm:resetForm() ➜ Resetting form state`);
		setUserEmail("");
		setDirectoryMemberDetails(undefined);
		setUserComments("");
		setEmailState({
			isValidating: false,
			isValid: false,
			type: "error",
			message: "Please enter a valid email address",
		});
		setUserDisplayName("");
		setNameState({
			isValidating: false,
			isValid: false,
			message: "",
		});
		setSelectedOrganisation(null);
		setOrganisationOptions([]);
		setOrganisationState({
			isValidating: false,
			isValid: false,
			message: "",
		});
		resetFormInactivity();
	};

	const submitRequest = async () => {
		console.debug(`RequestForm:submitRequest() ➜ Submitting request with Email: '${userEmail}', Name: '${userDisplayName}' and Organisation:`, selectedOrganisation);

		setWorkingState(true);
		setSubmittingState(true);
		toast?.current?.clear();
		try {
			const response = await submitUserAccessRequest({
				userEmail: userEmail || "",
				displayName: (userDisplayName || "").replace(/^ +| +$/gi, "").replace(/ {2,}/gi, " ").replace(/'{2,}/gi, "'"),
				organisation: selectedOrganisation || null,
				userComments: userComments || ""
			});
			console.debug(`RequestForm:submitRequest() ➜ Request submitted:`, response);
			if (!response.success) {
				toast?.current?.show({
					severity: "warn",
					summary: "Submit Request",
					detail: `${response?.message || "Your request could not be submitted, please try again later"}`,
					closable: true,
					sticky: true
				});
			} else {
				toast?.current?.show({
					severity: "success",
					summary: "Submit Request",
					detail: `Your request has been submitted successfully, we'll get back to you shortly.`,
					closable: true,
					life: 5000
				});
			}
		} catch (err) {
			console.error(`RequestForm:submitRequest() ➜ Error submitting request:`, err);
			const errMessage = digProperty(err, "message") || "";
			console.error(`RequestForm:submitRequest() ➜ Error message: ${errMessage}`);

			toast?.current?.show({
				severity: "error",
				summary: "Submit Request",
				detail: `An error occurred while submitting your request${!isEmptyString(errMessage) ? ` ➜ ${errMessage}` : ""}`,
				closable: true,
				sticky: true
			});
		} finally {
			setWorkingState(false);
			setSubmittingState(false);
			resetForm();
		}
	};

	const onEmailChanged = (e) => {
		const em = `${e?.target?.value || ""}`.trim();
		console.debug(`RequestForm:onEmailChanged() ➜ Email: ${em} [${userEmail || ""}]`);
		setUserEmail(em);
	};

	const onDisplayNameChanged = (e) => {
		const name = `${e?.target?.value || ""}`;
		console.debug(`RequestForm:onDisplayNameChanged() ➜ Email: ${name} [${userDisplayName || ""}]`);
		setUserDisplayName(name);
	};

	const validateUserDisplayName = async (userInput) => {
		const nameValue = `${userDisplayName || ""}`.trim();
		console.debug(`RequestForm:validateUserDisplayName() ➜ Name: ${userInput} [${nameValue || ""}]`);

		// replace leading and trailing spaces, and multiple spaces with a single space and multiple single-quotes with a single quote
		userInput = userInput.replace(/^ +| +$/gi, "").replace(/ {2,}/gi, " ").replace(/'{2,}/gi, "'");
		if (isEmptyString(userInput)) {
			setSelectedOrganisation(null);
			setNameState({
				isValidating: false,
				isValid: false,
				message: "Please enter your full name including first and last names",
			});
			setOrganisationState({
				isValidating: false,
				isValid: false,
				message: organisationState?.message || "",
			});
		} else if (
			(!!userInput.match(/[^a-z0-9.\-_' \(\)\[\]]+/gi))
			|| (!userInput.match(/[ ]+/gi))
		) {
			setSelectedOrganisation(null);
			setNameState({
				isValidating: false,
				isValid: false,
				message: "Valid characters for your name are alpha-numeric, 1 or more space characters, dot, hyphen, single-quote, underscore, square brackets, and parenthesis",
			});
			setOrganisationState({
				isValidating: false,
				isValid: false,
				message: organisationState?.message || "",
			});
		} else {
			setNameState({
				isValidating: false,
				isValid: true,
				message: "",
			});
		}
	};

	const validateUserEmail = async (userInput) => {
		const emState = `${userEmail || ""}`.trim();
		console.debug(`RequestForm:validateUserEmail() ➜ Email: ${userInput} [${emState || ""}]`);
		/* if (isEqualText(em, userEmail)) {
			// same as state value... NOOP
			return;
		} */

		setWorkingState(true);
		setEmailState({ ...emailState, isValidating: true });
		setDirectoryMemberDetails(undefined);
		try {
			if (isValidEmail(userInput)) {
				console.debug(`RequestForm:validateUserEmail() ➜ Pre-validating email: ${userInput}`);
				const responseData = await prevalidateAccountByEmail(userInput);
				console.debug(`RequestForm:validateUserEmail() ➜ Pre-validate response for ${userInput}:`, responseData);

				const isDirectoryMember = (responseData?.value === false);
				setDirectoryMemberDetails(responseData?.directoryMember || null);

				if (isDirectoryMember) {
					// email address IS already a directory account object
					setEmailState({
						isValidating: false,
						isValid: true,
						type: "info",
						message: `${CEmailAddressDirectoryMemberHelpSuffix}`,
					});
					setUserDisplayName(responseData?.directoryMember?.displayName || "");
					setNameState({
						isValidating: false,
						isValid: true,
						message: "",
					});
				} else {
					// email address IS NOT a directory account obejct
					setEmailState({
						isValidating: false,
						isValid: true,
						message: "",
					});
				}

				// get organisations for the email domain
				let organisations = await readOrganisationsByEmailDomain(userInput);
				// if the user is a directory member, filter out organisations they are already a member of
				if (isDirectoryMember && !isEmptyArray(responseData?.directoryMember?.memberOrganisations)) {
					const memberOrgIds = responseData.directoryMember.memberOrganisations || [];
					const filteredOrgs = (organisations || []).filter(org => {
						return !memberOrgIds.some(moId => isEqualText(moId, org.memberId));
					});
					console.debug(`RequestForm:validateUserEmail() ➜ Filtered organisations for directory member:`, filteredOrgs);
					organisations = filteredOrgs;
				}

				setOrganisationOptions(organisations || []);
				if (isEmptyArray(organisations)) {
					console.warn(`RequestForm:validateUserEmail() ➜ No organisations found for email: ${userInput}, isDirectoryMember: ${isDirectoryMember}`);
					setOrganisationState({
						...organisationState,
						message: isDirectoryMember ? `${COrganisationNoMoreToJoinHelpSuffix}` : `${COrganisationHelpSuffix}`,
					});
				}
			} else {
				console.debug(`RequestForm:validateUserEmail() ➜ Invalid email: ${userInput}`);
				setEmailState({
					isValidating: false,
					isValid: false,
					type: "error",
					message: "Please enter a valid email address",
				});
			}
		} catch (err) {
			console.error(`RequestForm:validateUserEmail() ➜ Error:`, err);
			const errMessage = digProperty(err, "message") || "";
			console.error(`RequestForm:validateUserEmail() ➜ Error message: ${errMessage}`);
			setEmailState({
				isValidating: false,
				isValid: false,
				type: "error",
				message: `An error occurred while validating the email address${!isEmptyString(errMessage) ? ` ➜ ${errMessage}` : ""}`,
			});
		} finally {
			setWorkingState(false);
		}
	};

	const validateAndSetOrganisation = async (organisation) => {
		console.debug(`RequestForm:validateAndSetOrganisation() ➜ Organisation:`, organisation);
		if (!organisation || isEmptyString(organisation?.memberId)) {
			if (!isEmptyString(userEmail)) {
				setOrganisationState({
					isValidating: false,
					isValid: false,
					message: `${COrganisationHelpNoneSelected}`,
				});
			}
			setSelectedOrganisation(null);
			return;
		}
		// check if the user is already a member of the selected organisation
		if (!isEmptyArray(directoryMemberDetails?.memberOrganisations)) {
			const isOrgMember = directoryMemberDetails.memberOrganisations.some(moId => isEqualText(moId, organisation.memberId));
			if (isOrgMember) {
				console.debug(`RequestForm:validateAndSetOrganisation() ➜ User is already a member of the selected organisation:`, organisation);
				setOrganisationState({
					isValidating: false,
					isValid: false,
					message: `You are already a member of the selected organisation, please contact us if you need further assistance.`,
				});
				setSelectedOrganisation(organisation);
				return;
			}
		}

		setWorkingState(true);
		setOrganisationState({ ...organisationState, isValidating: true });
		try {
			console.debug(`RequestForm:validateAndSetOrganisation() ➜ Validating ${userEmail} and ${organisation.memberId}:`);
			const isValid = await validateUserAccessRequest(userEmail, organisation.memberId);
			console.debug(`RequestForm:validateAndSetOrganisation() ➜ result:`, isValid);

			if (isValid) {
				if (organisation.memberStatus === COrganisationStatusSuspended) {
					setOrganisationState({
						isValidating: false,
						isValid: false,
						message: `${COrganisationHelpSuffix}`,
					});
				} else {
					setOrganisationState({
						isValidating: false,
						isValid: true,
						message: "",
					});
				}
			} else {
				setOrganisationState({
					isValidating: false,
					isValid: false,
					message: `An access request for this user and organisation has already been received, please contact us if you need further assistance.`,
				});
			}
			setSelectedOrganisation(organisation);
		} catch (err) {
			console.error(`RequestForm:validateAndSetOrganisation() ➜ Error:`, err);
			const errMessage = digProperty(err, "message") || "";
			console.error(`RequestForm:validateAndSetOrganisation() ➜ Error message: ${errMessage}`);
			setOrganisationState({
				isValidating: false,
				isValid: false,
				message: `An error occurred while validating the organisation${!isEmptyString(errMessage) ? ` ➜ ${errMessage}` : ""}`,
			});
			setSelectedOrganisation(null);
		} finally {
			setWorkingState(false);
		}
	}

	const organisationItemTemplate = (option) => {
		console.debug(`organisationItemTemplate`, option);
		return (
			<div className={"organisationItemTemplate"}>
				<span className={"organisationItemLabel"}>{option.memberName}</span>
				<span className={"organisationItemStatus"}>&nbsp;</span>
			</div>
		);
	};

	const UserMemberOrganisations = (props) => {
		if (
			isEmptyString(userEmail)
			|| isEmptyArray(directoryMemberDetails?.memberOrganisations)
			|| isEmptyArray(organisationOptions)
		) {
			return null;
		}
		const memberOfOrgs = organisationOptions.filter(orgOption =>
			directoryMemberDetails.memberOrganisations.some(moId => isEqualText(moId, orgOption.memberId))
		);
		if (isEmptyArray(memberOfOrgs)) {
			return null;
		}
		// render the users list of member organisations
		return (
			<div className={styles.memberOrganisationsContainer}>
				<label className="label">Member of</label>
				<div className={styles.memberOrganisationsContent}>
					{memberOfOrgs.map(org => (
						<Chip key={`memberOf-Org-${org.memberId}`} label={org.memberName} className="p-chip-info" />
					))}
				</div>
			</div>
		);
	};

	const isApiServiceDisconnected = isEmptyString(apiServiceContext?.serviceDetails?.solutionVersion);
	const { isApiConnecting } = apiServiceContext;
	return (
		<div className={styles.requestFormContainer}>
			<div className={styles.headerSection}>
				<h1 className="title">{title || ""}</h1>
				<h2 className="subtitle" style={{ paddingRight: "20px" }}
					dangerouslySetInnerHTML={{ __html: subTitle || "" }}
				/>
			</div>

			{/* user email address field */}
			<div className="field">
				<label className="label">Email address</label>
				<div className="columns">
					<div className="column">
						<div className="control">
							<div className={styles.fieldWithIcon}>
								<InputText
									tabIndex={"0"}
									required
									name="email"
									placeholder="Enter your email address"
									disabled={isApiConnecting || isApiServiceDisconnected || isWorking}
									invalid={!emailState.isValid}
									value={userEmail}
									onChange={(e) => onEmailChanged(e)}
									onBlur={(e) => {
										validateUserEmail(e?.target?.value);
									}}
									keyfilter={"email"}
									autoComplete='password'
									aria-autocomplete='none'
								/>
								{emailState.isValidating ? (
									<span className={"icon"}>
										<ProgressSpinner
											style={{ width: '26px', height: '26px', position: "relative", top: "-1px" }}
											strokeWidth="2"
											animationDuration="1.5s"
										/>
									</span>
								) : (
									<span className={`icon ${emailState.isValid ? "has-text-success" : "has-text-danger"}`}>
										<i className={`fas fa-${emailState.isValid ? "check" : "times"}-circle`}></i>
									</span>
								)}
							</div>
							{!emailState.isValidating && (!emailState.isValid || (!isEmptyString(emailState?.type))) && (
								<p className={`help ${!emailState.isValid || (emailState?.type === "error") ? "is-danger" : (emailState?.type === "info") ? "is-info" : "is-warning"}`}>
									<span dangerouslySetInnerHTML={{ __html: emailState.message }} />
								</p>
							)}
						</div>
					</div>
					<div className="column is-1 field"></div>
				</div>
			</div>

			{/* user full name field */}
			<div className="field">
				<label tabIndex={"0"} className="label">Full name</label>
				<div className="columns">
					<div className="column">
						<div className="control">
							<div className={styles.fieldWithIcon}>
								<InputText
									required
									name="displayname"
									readOnly={!isNullOrUndefined(directoryMemberDetails)}
									placeholder="Enter your full name"
									disabled={isApiConnecting || isApiServiceDisconnected || !emailState.isValid || isWorking}
									invalid={!nameState.isValid}
									value={userDisplayName}
									onChange={(e) => onDisplayNameChanged(e)}
									onBlur={(e) => {
										validateUserDisplayName(e?.target?.value);
									}}
									autoComplete='password'
									aria-autocomplete='none'
								/>
								{nameState.isValidating ? (
									<span className={"icon"}>
										<ProgressSpinner
											style={{ width: '26px', height: '26px', position: "relative", top: "-1px" }}
											strokeWidth="2"
											animationDuration="1.5s"
										/>
									</span>
								) : (
									<span className={`icon ${nameState.isValid ? "has-text-success" : "has-text-danger"}`}>
										<i className={`fas fa-${nameState.isValid ? "check" : "times"}-circle`}></i>
									</span>
								)}
							</div>
							{!nameState.isValidating && !nameState.isValid && (
								<p className="help is-danger">
									<span dangerouslySetInnerHTML={{ __html: nameState.message }} />
								</p>
							)}
							<UserMemberOrganisations />
						</div>
					</div>
					<div className="column is-1 field"></div>
				</div>
			</div>

			{/* organisations select dropdown field */}
			<div className="field">
				<label tabIndex={"0"} className="label">Organisation</label>
				<div className="columns">
					<div className="column">
						<div className="control">
							<div className={styles.fieldWithIcon}>
								<Dropdown
									disabled={isApiConnecting || isApiServiceDisconnected || isWorking || !emailState.isValid || !nameState.isValid || isEmptyArray(organisationOptions)}
									invalid={!organisationState.isValid}
									value={selectedOrganisation?.memberId || ""}
									onChange={(e) => {
										console.debug(`RequestForm:Dropdown onChange ➜ Selected Organisation:`, e);
										const org = (organisationOptions || []).find(o => isEqualText(o.memberId, e.value));
										if (org) {
											console.debug(`RequestForm:Dropdown onChange ➜ Found Organisation:`, org);
										} else {
											console.warn(`RequestForm:Dropdown onChange ➜ Organisation not found for value: ${e.value}`);
										}
										validateAndSetOrganisation(org || null);
									}}
									showClear
									itemTemplate={organisationItemTemplate}
									options={organisationOptions}
									optionLabel="memberName"
									optionValue="memberId"
									placeholder="Select an organisation"
								/>
								{organisationState.isValidating ? (
									<span className={"icon"}>
										<ProgressSpinner
											style={{ width: '26px', height: '26px', position: "relative", top: "-1px" }}
											strokeWidth="2"
											animationDuration="1.5s"
										/>
									</span>
								) : (
									<span className={`icon ${organisationState.isValid ? "has-text-success" : "has-text-danger"}`}>
										<i className={`fas fa-${organisationState.isValid ? "check" : "times"}-circle`}></i>
									</span>
								)}
							</div>
							{!organisationState.isValidating && !organisationState.isValid && (
								<p className={`help ${(selectedOrganisation?.memberStatus || "") === COrganisationStatusSuspended ? "is-info-bold" : "is-danger"}`}>
									<span dangerouslySetInnerHTML={{ __html: organisationState.message }} />
								</p>
							)}
						</div>
					</div>
					<div className="column is-1 field"></div>
				</div>
			</div>

			{/* comments field */}
			<div className="field">
				<label tabIndex={"0"} className="label">Comments</label>
				<div className="columns">
					<div className="column">
						<div className="control">
							<textarea
								value={userComments || ""}
								disabled={isApiConnecting || isApiServiceDisconnected || isWorking || !emailState.isValid || !nameState.isValid || !organisationState.isValid}
								onChange={(e) => {
									console.debug(`RequestForm:Textarea onChange ➜ Comments: ${e.target.value}`);
									setUserComments(e.target.value || "");
								}}
								className="textarea has-fixed-size"
								required
								resizable="false"
								rows="2"
								placeholder="You can also include any additional information in your request"
							/>
						</div>
					</div>
					<div className="column is-1 field">
					</div>
				</div>
			</div>

			<div className="field">
				<div className="columns">
					<div className="column">
						<div className="control">
							<Button
								className={"p-button-wide"}
								disabled={isApiConnecting || isApiServiceDisconnected || isWorking || !emailState.isValid || !nameState.isValid || !organisationState.isValid}
								onClick={() => {
									resetFormInactivityTimeout();
									submitRequest();
								}}
								loading={isWorking}
								label="Submit Request"
							/>
							<Button
								className={"p-button-wide"}
								disabled={isApiConnecting || isApiServiceDisconnected}
								onClick={() => {
									resetForm();
								}}
								loading={isWorking}
								label="Reset Form"
								style={{ marginLeft: "10px", display: "none" }}
							/>
							<div className={styles.progressBar}>
								{isSubmitting && (
									<ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<Toast ref={toast} position="top-center" className="p-toast-wide" />
		</div>
	);
};
